/**
 * RFC-193 Phase 4 — pure display contract for a requirement anchor's derived
 * state. The server computes the `AnchorState` (mirrors
 * server/src/services/compliance/requirement-anchor.state.ts) and sends it on
 * the anchor tree node; the UI only maps state → label / tone / whether the
 * manual ask is shown. Kept pure + dependency-free so it's unit-testable in the
 * web package's logic-only vitest setup (no DOM).
 */

export type AnchorState =
  | "NOT_APPLICABLE"
  | "NO_APPLICABLE_RESOURCES"
  | "SATISFIED_WITH_EXCEPTION"
  | "AUTOMATION_CONTRADICTS"
  | "MANUALLY_SATISFIED"
  | "VERIFIED_AUTOMATICALLY"
  | "EVIDENCE_READY_FOR_REVIEW"
  | "AUTOMATION_AVAILABLE"
  | "NEEDS_MANUAL_EVIDENCE";

/** Semantic tone → mapped to concrete color classes by the badge component. */
export type AnchorTone = "success" | "danger" | "warning" | "info" | "muted";

/** Tone → Tailwind badge classes, shared by every anchor-state badge. */
export const ANCHOR_TONE_CLASS: Record<AnchorTone, string> = {
  success: "bg-status-green-bg text-status-green-strong",
  danger: "bg-status-red-bg text-status-red-strong",
  warning: "bg-status-orange-bg text-status-orange-strong",
  info: "bg-status-blue-bg text-status-blue-strong",
  muted: "bg-status-gray-bg text-status-gray-strong",
};

interface AnchorDisplay {
  label: string;
  tone: AnchorTone;
  /** Whether the requirement still presents a manual evidence ask in the UI. */
  manualAskOpen: boolean;
  /** Whether it counts as satisfied / unsatisfied / excluded for readiness. */
  readiness: "satisfied" | "unsatisfied" | "not_applicable";
}

export const ANCHOR_STATE_DISPLAY: Record<AnchorState, AnchorDisplay> = {
  VERIFIED_AUTOMATICALLY: {
    label: "Verified automatically",
    tone: "success",
    manualAskOpen: false,
    readiness: "satisfied",
  },
  MANUALLY_SATISFIED: {
    label: "Evidence provided",
    tone: "success",
    manualAskOpen: false,
    readiness: "satisfied",
  },
  SATISFIED_WITH_EXCEPTION: {
    label: "Satisfied with exception",
    tone: "info",
    manualAskOpen: false,
    readiness: "satisfied",
  },
  AUTOMATION_CONTRADICTS: {
    label: "Automation contradicts",
    tone: "danger",
    manualAskOpen: true,
    readiness: "unsatisfied",
  },
  EVIDENCE_READY_FOR_REVIEW: {
    label: "Evidence ready for review",
    tone: "info",
    manualAskOpen: true,
    readiness: "unsatisfied",
  },
  AUTOMATION_AVAILABLE: {
    label: "Automation available",
    tone: "warning",
    manualAskOpen: true,
    readiness: "unsatisfied",
  },
  NEEDS_MANUAL_EVIDENCE: {
    label: "Needs evidence",
    tone: "warning",
    manualAskOpen: true,
    readiness: "unsatisfied",
  },
  NO_APPLICABLE_RESOURCES: {
    label: "No applicable resources",
    tone: "muted",
    manualAskOpen: true, // surfaced for "confirm N/A or upload" — never silent
    readiness: "not_applicable",
  },
  NOT_APPLICABLE: {
    label: "Not applicable",
    tone: "muted",
    manualAskOpen: false,
    readiness: "not_applicable",
  },
};

export const anchorStateLabel = (s: AnchorState): string =>
  ANCHOR_STATE_DISPLAY[s].label;
export const anchorStateTone = (s: AnchorState): AnchorTone =>
  ANCHOR_STATE_DISPLAY[s].tone;
export const anchorManualAskOpen = (s: AnchorState): boolean =>
  ANCHOR_STATE_DISPLAY[s].manualAskOpen;
export const anchorReadiness = (s: AnchorState) =>
  ANCHOR_STATE_DISPLAY[s].readiness;

/**
 * Count requirement anchors by readiness bucket — the §5.4 counter reduction,
 * so stat cards count REQUIREMENTS (anchors) once, not each rolled-up child CES.
 * `not_applicable` is excluded from the denominator (matches the server).
 */
export function countAnchorReadiness(
  anchors: ReadonlyArray<{ anchorState: AnchorState }>,
): { total: number; satisfied: number; unsatisfied: number; notApplicable: number } {
  let satisfied = 0;
  let unsatisfied = 0;
  let notApplicable = 0;
  for (const a of anchors) {
    const r = ANCHOR_STATE_DISPLAY[a.anchorState].readiness;
    if (r === "satisfied") satisfied++;
    else if (r === "unsatisfied") unsatisfied++;
    else notApplicable++;
  }
  return {
    total: satisfied + unsatisfied, // denominator excludes N/A
    satisfied,
    unsatisfied,
    notApplicable,
  };
}
